import * as Y from "yjs";
import { newBlockId, newCommentId, newGalleryItemId, newTodoItemId, } from "../blocks.js";
import { WaveBlockKind } from "../types.js";
export const WAVE_YDOC_BLOCKS_KEY = "blocks";
export const WAVE_YDOC_COMMENTS_KEY = "comments";
export const REMOTE_YJS_ORIGIN = "wave-remote";
export const LOCAL_YJS_ORIGIN = "wave-local";
const REACTION_KEY_SEP = "\u0001";
/** Flat keys at document root (byte-compatible with iOS JavaScriptCore Yjs engine). */
export function blockTextKey(blockId) {
    return `blk/${blockId}/text`;
}
export function blockTitleKey(blockId) {
    return `blk/${blockId}/title`;
}
export function blockMetaKey(blockId) {
    return `blk/${blockId}/meta`;
}
export function blockVotesKey(blockId) {
    return `blk/${blockId}/votes`;
}
export function blockApprovalsKey(blockId) {
    return `blk/${blockId}/approvals`;
}
export function blockChoicesKey(blockId) {
    return `blk/${blockId}/choices`;
}
export function blockTodoItemsKey(blockId) {
    return `blk/${blockId}/todo/items`;
}
export function blockTodoItemTitleKey(blockId, itemId) {
    return `blk/${blockId}/todo/${itemId}/title`;
}
export function blockTodoItemMetaKey(blockId, itemId) {
    return `blk/${blockId}/todo/${itemId}/meta`;
}
export function blockGalleryItemsKey(blockId) {
    return `blk/${blockId}/gallery/items`;
}
export function blockGalleryItemMetaKey(blockId, itemId) {
    return `blk/${blockId}/gallery/${itemId}/meta`;
}
export function blockGalleryItemCaptionKey(blockId, itemId) {
    return `blk/${blockId}/gallery/${itemId}/caption`;
}
export function commentMetaKey(commentId) {
    return `cmt/${commentId}/meta`;
}
export function commentReactionsKey(commentId) {
    return `cmt/${commentId}/reactions`;
}
export function reactionMapKey(emoji, sub) {
    return `${emoji}${REACTION_KEY_SEP}${sub}`;
}
/** Normalize raw meta.kind to a known enum value (shared with iOS engine bundle). */
export function parseBlockKind(raw) {
    const value = `${raw}`.trim().toUpperCase();
    if (Object.values(WaveBlockKind).includes(value)) {
        return value;
    }
    return WaveBlockKind.Text;
}
export function createWaveYDoc() {
    const doc = new Y.Doc();
    doc.getArray(WAVE_YDOC_BLOCKS_KEY);
    doc.getArray(WAVE_YDOC_COMMENTS_KEY);
    return doc;
}
export function getBlockIdsArray(doc) {
    return doc.getArray(WAVE_YDOC_BLOCKS_KEY);
}
export function getBlockText(doc, blockId) {
    return doc.getText(blockTextKey(blockId));
}
export function getBlockTitle(doc, blockId) {
    return doc.getText(blockTitleKey(blockId));
}
export function getBlockMetaMap(doc, blockId) {
    return doc.getMap(blockMetaKey(blockId));
}
export function getBlockVotesMap(doc, blockId) {
    return doc.getMap(blockVotesKey(blockId));
}
export function getBlockApprovalsMap(doc, blockId) {
    return doc.getMap(blockApprovalsKey(blockId));
}
export function getBlockChoicesArray(doc, blockId) {
    return doc.getArray(blockChoicesKey(blockId));
}
export function getBlockTodoItemsArray(doc, blockId) {
    return doc.getArray(blockTodoItemsKey(blockId));
}
export function getBlockTodoItemTitle(doc, blockId, itemId) {
    return doc.getText(blockTodoItemTitleKey(blockId, itemId));
}
export function getBlockTodoItemMetaMap(doc, blockId, itemId) {
    return doc.getMap(blockTodoItemMetaKey(blockId, itemId));
}
export function getBlockGalleryItemsArray(doc, blockId) {
    return doc.getArray(blockGalleryItemsKey(blockId));
}
export function getBlockGalleryItemMetaMap(doc, blockId, itemId) {
    return doc.getMap(blockGalleryItemMetaKey(blockId, itemId));
}
export function getBlockGalleryItemCaption(doc, blockId, itemId) {
    return doc.getText(blockGalleryItemCaptionKey(blockId, itemId));
}
export function getCommentIdsArray(doc) {
    return doc.getArray(WAVE_YDOC_COMMENTS_KEY);
}
export function getCommentMetaMap(doc, commentId) {
    return doc.getMap(commentMetaKey(commentId));
}
export function getCommentReactionsMap(doc, commentId) {
    return doc.getMap(commentReactionsKey(commentId));
}
function writePayloadToDoc(doc, block) {
    const meta = getBlockMetaMap(doc, block.id);
    meta.set("id", block.id);
    meta.set("kind", block.kind);
    meta.set("sortIndex", block.sortIndex);
    if (block.createdAt)
        meta.set("createdAt", block.createdAt);
    if (block.updatedAt)
        meta.set("updatedAt", block.updatedAt);
    const payload = block.payload;
    if (payload.level != null)
        meta.set("level", payload.level);
    if (payload.taskDone != null)
        meta.set("taskDone", payload.taskDone);
    if (payload.assigneeSub != null)
        meta.set("assigneeSub", payload.assigneeSub);
    if (payload.dueDateISO != null)
        meta.set("dueDateISO", payload.dueDateISO);
    if (payload.refId != null)
        meta.set("refId", payload.refId);
    if (payload.refTitle != null)
        meta.set("refTitle", payload.refTitle);
    if (payload.refApp != null)
        meta.set("refApp", payload.refApp);
    if (payload.requiredSubs != null)
        meta.set("requiredSubs", payload.requiredSubs);
    if (payload.fileName != null)
        meta.set("fileName", payload.fileName);
    if (payload.mimeType != null)
        meta.set("mimeType", payload.mimeType);
    if (payload.s3Key != null)
        meta.set("s3Key", payload.s3Key);
    if (payload.fileSize != null)
        meta.set("fileSize", payload.fileSize);
    if (payload.uploadStatus != null)
        meta.set("uploadStatus", payload.uploadStatus);
    if (payload.ragStatus != null)
        meta.set("ragStatus", payload.ragStatus);
    if (payload.ragReason != null)
        meta.set("ragReason", payload.ragReason);
    if (payload.ragChunks != null)
        meta.set("ragChunks", payload.ragChunks);
    if (payload.authorSub != null)
        meta.set("authorSub", payload.authorSub);
    if (payload.authorName != null)
        meta.set("authorName", payload.authorName);
    if (payload.widgetIndex != null)
        meta.set("widgetIndex", payload.widgetIndex);
    if (payload.projectId != null)
        meta.set("projectId", payload.projectId);
    if (payload.projectName != null)
        meta.set("projectName", payload.projectName);
    if (payload.parentId != null)
        meta.set("parentId", payload.parentId);
    if (payload.relation != null)
        meta.set("relation", payload.relation);
    if (payload.archived != null)
        meta.set("archived", payload.archived);
    if (payload.archivedAt != null)
        meta.set("archivedAt", payload.archivedAt);
    if (payload.wikiRef != null) {
        meta.set("wikiRef", JSON.stringify({
            harborProjectId: payload.wikiRef.harborProjectId ?? "",
            slug: payload.wikiRef.slug ?? "",
            title: payload.wikiRef.title ?? "",
            sourceUpdatedAt: payload.wikiRef.sourceUpdatedAt ?? null,
        }));
    }
    if (payload.todoItems?.length) {
        const itemsArr = getBlockTodoItemsArray(doc, block.id);
        while (itemsArr.length > 0)
            itemsArr.delete(0, 1);
        for (const item of payload.todoItems) {
            writeTodoItemToDoc(doc, block.id, item);
            itemsArr.push([item.id]);
        }
    }
    if (payload.galleryItems?.length) {
        const galleryArr = getBlockGalleryItemsArray(doc, block.id);
        while (galleryArr.length > 0)
            galleryArr.delete(0, 1);
        for (const item of payload.galleryItems) {
            writeGalleryItemToDoc(doc, block.id, item);
            galleryArr.push([item.id]);
        }
    }
    if (payload.text != null) {
        const t = getBlockText(doc, block.id);
        if (t.length > 0)
            t.delete(0, t.length);
        if (payload.text.length > 0)
            t.insert(0, payload.text);
    }
    if (payload.title != null) {
        const t = getBlockTitle(doc, block.id);
        if (t.length > 0)
            t.delete(0, t.length);
        if (payload.title.length > 0)
            t.insert(0, payload.title);
    }
    if (payload.choices?.length) {
        const choices = getBlockChoicesArray(doc, block.id);
        while (choices.length > 0)
            choices.delete(0, 1);
        for (const c of payload.choices)
            choices.push([c]);
    }
    if (payload.votes) {
        const votes = getBlockVotesMap(doc, block.id);
        for (const [sub, choice] of Object.entries(payload.votes)) {
            votes.set(sub, choice);
        }
    }
    if (payload.approvals) {
        const approvals = getBlockApprovalsMap(doc, block.id);
        for (const [sub, ok] of Object.entries(payload.approvals)) {
            approvals.set(sub, ok);
        }
    }
}
function writeTodoItemToDoc(doc, blockId, item) {
    const meta = getBlockTodoItemMetaMap(doc, blockId, item.id);
    meta.set("id", item.id);
    meta.set("done", item.done);
    if (item.assignees.length)
        meta.set("assignees", item.assignees);
    if (item.dueDateISO)
        meta.set("dueDateISO", item.dueDateISO);
    if (item.priority)
        meta.set("priority", item.priority);
    if (item.createdAt)
        meta.set("createdAt", item.createdAt);
    if (item.authorSub)
        meta.set("authorSub", item.authorSub);
    const title = getBlockTodoItemTitle(doc, blockId, item.id);
    if (title.length > 0)
        title.delete(0, title.length);
    if (item.title.length > 0)
        title.insert(0, item.title);
}
function todoItemFromDoc(doc, blockId, itemId) {
    const meta = getBlockTodoItemMetaMap(doc, blockId, itemId);
    const id = meta.get("id");
    if (typeof id !== "string")
        return null;
    const done = meta.get("done");
    const assigneesRaw = meta.get("assignees");
    const assignees = Array.isArray(assigneesRaw)
        ? assigneesRaw.filter((s) => typeof s === "string")
        : [];
    const dueDateISO = meta.get("dueDateISO");
    const priority = meta.get("priority");
    const createdAt = meta.get("createdAt");
    const authorSub = meta.get("authorSub");
    const title = getBlockTodoItemTitle(doc, blockId, itemId).toString();
    return {
        id,
        title,
        done: done === true,
        assignees,
        dueDateISO: typeof dueDateISO === "string" ? dueDateISO : undefined,
        priority: typeof priority === "string" ? priority : undefined,
        createdAt: typeof createdAt === "string" ? createdAt : undefined,
        authorSub: typeof authorSub === "string" ? authorSub : undefined,
    };
}
function todoItemsFromDoc(doc, blockId) {
    const arr = getBlockTodoItemsArray(doc, blockId);
    const result = [];
    for (let i = 0; i < arr.length; i++) {
        const itemId = arr.get(i);
        if (typeof itemId !== "string")
            continue;
        const item = todoItemFromDoc(doc, blockId, itemId);
        if (item)
            result.push(item);
    }
    return result;
}
function writeGalleryItemToDoc(doc, blockId, item) {
    const meta = getBlockGalleryItemMetaMap(doc, blockId, item.id);
    meta.set("id", item.id);
    if (item.s3Key)
        meta.set("s3Key", item.s3Key);
    if (item.mimeType)
        meta.set("mimeType", item.mimeType);
    if (item.fileName)
        meta.set("fileName", item.fileName);
    if (item.fileSize != null)
        meta.set("fileSize", item.fileSize);
    if (item.uploadStatus)
        meta.set("uploadStatus", item.uploadStatus);
    if (item.createdAt)
        meta.set("createdAt", item.createdAt);
    const caption = getBlockGalleryItemCaption(doc, blockId, item.id);
    if (caption.length > 0)
        caption.delete(0, caption.length);
    if (item.caption?.length)
        caption.insert(0, item.caption);
}
function galleryItemFromDoc(doc, blockId, itemId) {
    const meta = getBlockGalleryItemMetaMap(doc, blockId, itemId);
    const id = meta.get("id");
    if (typeof id !== "string")
        return null;
    const s3Key = meta.get("s3Key");
    const mimeType = meta.get("mimeType");
    const fileName = meta.get("fileName");
    const fileSize = meta.get("fileSize");
    const uploadStatus = meta.get("uploadStatus");
    const createdAt = meta.get("createdAt");
    const caption = getBlockGalleryItemCaption(doc, blockId, itemId).toString();
    return {
        id,
        s3Key: typeof s3Key === "string" ? s3Key : undefined,
        mimeType: typeof mimeType === "string" ? mimeType : undefined,
        fileName: typeof fileName === "string" ? fileName : undefined,
        fileSize: typeof fileSize === "number" ? fileSize : undefined,
        caption: caption.length > 0 ? caption : undefined,
        uploadStatus: typeof uploadStatus === "string"
            ? uploadStatus
            : undefined,
        createdAt: typeof createdAt === "string" ? createdAt : undefined,
    };
}
function galleryItemsFromDoc(doc, blockId) {
    const arr = getBlockGalleryItemsArray(doc, blockId);
    const result = [];
    for (let i = 0; i < arr.length; i++) {
        const itemId = arr.get(i);
        if (typeof itemId !== "string")
            continue;
        const item = galleryItemFromDoc(doc, blockId, itemId);
        if (item)
            result.push(item);
    }
    return result;
}
function nextTodoListWidgetIndex(doc) {
    let max = 0;
    const ids = getBlockIdsArray(doc);
    for (let i = 0; i < ids.length; i++) {
        const id = ids.get(i);
        if (typeof id !== "string")
            continue;
        const meta = getBlockMetaMap(doc, id);
        if (parseBlockKind(String(meta.get("kind") ?? "")) !== WaveBlockKind.TodoList) {
            continue;
        }
        const wi = meta.get("widgetIndex");
        if (typeof wi === "number" && wi > max)
            max = wi;
    }
    return max + 1;
}
export function waveBlockFromDoc(doc, blockId, sortIndex) {
    const meta = getBlockMetaMap(doc, blockId);
    const kind = parseBlockKind(String(meta.get("kind") ?? WaveBlockKind.Text));
    const payload = {};
    const text = getBlockText(doc, blockId).toString();
    if (text.length)
        payload.text = text;
    const title = getBlockTitle(doc, blockId).toString();
    if (title.length)
        payload.title = title;
    const level = meta.get("level");
    if (typeof level === "number")
        payload.level = level;
    const taskDone = meta.get("taskDone");
    if (typeof taskDone === "boolean")
        payload.taskDone = taskDone;
    const assigneeSub = meta.get("assigneeSub");
    if (typeof assigneeSub === "string")
        payload.assigneeSub = assigneeSub;
    const dueDateISO = meta.get("dueDateISO");
    if (typeof dueDateISO === "string")
        payload.dueDateISO = dueDateISO;
    const refId = meta.get("refId");
    if (typeof refId === "string")
        payload.refId = refId;
    const refTitle = meta.get("refTitle");
    if (typeof refTitle === "string")
        payload.refTitle = refTitle;
    const refApp = meta.get("refApp");
    if (typeof refApp === "string")
        payload.refApp = refApp;
    const fileName = meta.get("fileName");
    if (typeof fileName === "string")
        payload.fileName = fileName;
    const mimeType = meta.get("mimeType");
    if (typeof mimeType === "string")
        payload.mimeType = mimeType;
    const s3Key = meta.get("s3Key");
    if (typeof s3Key === "string")
        payload.s3Key = s3Key;
    const fileSize = meta.get("fileSize");
    if (typeof fileSize === "number")
        payload.fileSize = fileSize;
    const uploadStatus = meta.get("uploadStatus");
    if (typeof uploadStatus === "string") {
        payload.uploadStatus = uploadStatus;
    }
    const ragStatus = meta.get("ragStatus");
    if (typeof ragStatus === "string") {
        payload.ragStatus = ragStatus;
    }
    const ragReason = meta.get("ragReason");
    if (typeof ragReason === "string")
        payload.ragReason = ragReason;
    const ragChunks = meta.get("ragChunks");
    if (typeof ragChunks === "number")
        payload.ragChunks = ragChunks;
    const authorSub = meta.get("authorSub");
    if (typeof authorSub === "string")
        payload.authorSub = authorSub;
    const authorName = meta.get("authorName");
    if (typeof authorName === "string")
        payload.authorName = authorName;
    const widgetIndex = meta.get("widgetIndex");
    if (typeof widgetIndex === "number")
        payload.widgetIndex = widgetIndex;
    const projectId = meta.get("projectId");
    if (typeof projectId === "string")
        payload.projectId = projectId;
    const projectName = meta.get("projectName");
    if (typeof projectName === "string")
        payload.projectName = projectName;
    const parentId = meta.get("parentId");
    if (typeof parentId === "string" && parentId.length > 0)
        payload.parentId = parentId;
    const relation = meta.get("relation");
    if (typeof relation === "string" && relation.length > 0)
        payload.relation = relation;
    const archived = meta.get("archived");
    if (typeof archived === "boolean")
        payload.archived = archived;
    const archivedAt = meta.get("archivedAt");
    if (typeof archivedAt === "string" && archivedAt.length > 0)
        payload.archivedAt = archivedAt;
    const agentStatus = meta.get("agentStatus");
    if (typeof agentStatus === "string")
        payload.agentStatus = agentStatus;
    const agentJobId = meta.get("agentJobId");
    if (typeof agentJobId === "string")
        payload.agentJobId = agentJobId;
    const launchedBy = meta.get("launchedBy");
    if (typeof launchedBy === "string")
        payload.launchedBy = launchedBy;
    const agentIcon = meta.get("agentIcon");
    if (typeof agentIcon === "string")
        payload.agentIcon = agentIcon;
    const agentIconS3Key = meta.get("agentIconS3Key");
    if (typeof agentIconS3Key === "string")
        payload.agentIconS3Key = agentIconS3Key;
    const agentHandlersJson = meta.get("agentHandlersJson");
    if (typeof agentHandlersJson === "string")
        payload.agentHandlersJson = agentHandlersJson;
    const agentQuestion = meta.get("agentQuestion");
    if (typeof agentQuestion === "string")
        payload.agentQuestion = agentQuestion;
    const wikiRefRaw = meta.get("wikiRef");
    if (typeof wikiRefRaw === "string" && wikiRefRaw.length > 0) {
        try {
            const parsed = JSON.parse(wikiRefRaw);
            if (parsed && typeof parsed === "object" && parsed.slug) {
                payload.wikiRef = {
                    harborProjectId: String(parsed.harborProjectId ?? ""),
                    slug: String(parsed.slug),
                    title: String(parsed.title ?? parsed.slug),
                    sourceUpdatedAt: parsed.sourceUpdatedAt ?? null,
                };
            }
        }
        catch {
            /* ignore malformed wikiRef */
        }
    }
    if (kind === WaveBlockKind.TodoList) {
        const items = todoItemsFromDoc(doc, blockId);
        if (items.length)
            payload.todoItems = items;
    }
    if (kind === WaveBlockKind.Gallery) {
        const items = galleryItemsFromDoc(doc, blockId);
        if (items.length)
            payload.galleryItems = items;
    }
    const requiredSubs = meta.get("requiredSubs");
    if (Array.isArray(requiredSubs)) {
        payload.requiredSubs = requiredSubs.filter((s) => typeof s === "string");
    }
    const choicesArr = getBlockChoicesArray(doc, blockId);
    const choices = choicesArr
        .toArray()
        .filter((c) => typeof c === "string");
    if (choices.length)
        payload.choices = choices;
    const votesMap = getBlockVotesMap(doc, blockId);
    if (votesMap.size > 0) {
        const votes = {};
        votesMap.forEach((value, key) => {
            if (typeof value === "string")
                votes[key] = value;
        });
        payload.votes = votes;
    }
    const approvalsMap = getBlockApprovalsMap(doc, blockId);
    if (approvalsMap.size > 0) {
        const approvals = {};
        approvalsMap.forEach((value, key) => {
            if (typeof value === "boolean")
                approvals[key] = value;
        });
        payload.approvals = approvals;
    }
    return {
        id: blockId,
        kind,
        payload,
        sortIndex,
        createdAt: typeof meta.get("createdAt") === "string" ? meta.get("createdAt") : undefined,
        updatedAt: typeof meta.get("updatedAt") === "string" ? meta.get("updatedAt") : undefined,
    };
}
export function blocksFromYDoc(doc) {
    const ids = getBlockIdsArray(doc);
    const result = [];
    for (let i = 0; i < ids.length; i++) {
        const id = ids.get(i);
        if (typeof id === "string")
            result.push(waveBlockFromDoc(doc, id, i));
    }
    return result;
}
function reactionsFromMap(reactionsMap) {
    const grouped = {};
    reactionsMap.forEach((active, key) => {
        if (!active)
            return;
        const sep = key.indexOf(REACTION_KEY_SEP);
        if (sep <= 0)
            return;
        const emoji = key.slice(0, sep);
        const sub = key.slice(sep + REACTION_KEY_SEP.length);
        if (!emoji || !sub)
            return;
        if (!grouped[emoji])
            grouped[emoji] = [];
        grouped[emoji].push(sub);
    });
    return grouped;
}
export function commentFromDoc(doc, commentId) {
    const meta = getCommentMetaMap(doc, commentId);
    const id = meta.get("id");
    const blockAnchorId = meta.get("blockAnchorId");
    const authorSub = meta.get("authorSub");
    const body = meta.get("body");
    const createdAt = meta.get("createdAt");
    if (typeof id !== "string" ||
        typeof blockAnchorId !== "string" ||
        typeof authorSub !== "string" ||
        typeof body !== "string" ||
        typeof createdAt !== "string") {
        return null;
    }
    const parentRaw = meta.get("parentId");
    const parentId = parentRaw == null || parentRaw === "" ? null : String(parentRaw);
    const authorName = typeof meta.get("authorName") === "string"
        ? meta.get("authorName")
        : authorSub;
    const reactions = reactionsFromMap(getCommentReactionsMap(doc, commentId));
    const galleryItemRaw = meta.get("galleryItemId");
    const galleryItemId = galleryItemRaw == null || galleryItemRaw === ""
        ? null
        : String(galleryItemRaw);
    return {
        id,
        blockAnchorId,
        parentId,
        galleryItemId,
        authorSub,
        authorName,
        body,
        createdAt,
        reactions,
    };
}
export function commentsFromYDoc(doc) {
    const ids = getCommentIdsArray(doc);
    const result = [];
    for (let i = 0; i < ids.length; i++) {
        const id = ids.get(i);
        if (typeof id !== "string")
            continue;
        const comment = commentFromDoc(doc, id);
        if (comment)
            result.push(comment);
    }
    return result;
}
export function postComment(doc, input) {
    const id = newCommentId();
    const createdAt = new Date().toISOString();
    let parentId = input.parentId?.trim() || null;
    if (parentId) {
        const parent = commentFromDoc(doc, parentId);
        if (!parent)
            parentId = null;
        else if (parent.parentId)
            parentId = parent.parentId;
    }
    const galleryItemId = input.galleryItemId?.trim() || null;
    const comment = {
        id,
        blockAnchorId: input.blockAnchorId,
        parentId,
        galleryItemId,
        authorSub: input.authorSub,
        authorName: input.authorName.trim() || input.authorSub,
        body: input.body.trim(),
        createdAt,
        reactions: {},
    };
    doc.transact(() => {
        const meta = getCommentMetaMap(doc, id);
        meta.set("id", comment.id);
        meta.set("blockAnchorId", comment.blockAnchorId);
        meta.set("parentId", comment.parentId ?? "");
        meta.set("galleryItemId", comment.galleryItemId ?? "");
        meta.set("authorSub", comment.authorSub);
        meta.set("authorName", comment.authorName);
        meta.set("body", comment.body);
        meta.set("createdAt", comment.createdAt);
        getCommentIdsArray(doc).push([id]);
    }, LOCAL_YJS_ORIGIN);
    return comment;
}
export function toggleReaction(doc, commentId, emoji, sub) {
    doc.transact(() => {
        const reactions = getCommentReactionsMap(doc, commentId);
        const key = reactionMapKey(emoji, sub);
        if (reactions.get(key) === true) {
            reactions.delete(key);
        }
        else {
            reactions.set(key, true);
        }
    }, LOCAL_YJS_ORIGIN);
}
export function removeComment(doc, commentId) {
    doc.transact(() => {
        const arr = getCommentIdsArray(doc);
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === commentId) {
                arr.delete(i, 1);
                break;
            }
        }
        getCommentMetaMap(doc, commentId).clear();
        getCommentReactionsMap(doc, commentId).clear();
    }, LOCAL_YJS_ORIGIN);
}
export function loadBlocksIntoYDoc(doc, blocks) {
    doc.transact(() => {
        const arr = getBlockIdsArray(doc);
        while (arr.length > 0)
            arr.delete(0, 1);
        const sorted = [...blocks].sort((a, b) => a.sortIndex - b.sortIndex);
        for (const block of sorted) {
            writePayloadToDoc(doc, block);
            arr.push([block.id]);
        }
    }, LOCAL_YJS_ORIGIN);
}
/** Resolve parent to a single-level root (parent must not itself be nested). */
export function resolveRootParentId(doc, parentId) {
    const meta = getBlockMetaMap(doc, parentId);
    const grandParent = meta.get("parentId");
    if (typeof grandParent === "string" && grandParent.length > 0) {
        return grandParent;
    }
    return parentId;
}
export function getChildBlockIds(doc, parentId) {
    const arr = getBlockIdsArray(doc);
    const result = [];
    for (let i = 0; i < arr.length; i++) {
        const id = arr.get(i);
        if (typeof id !== "string")
            continue;
        const meta = getBlockMetaMap(doc, id);
        const pid = meta.get("parentId");
        if (typeof pid === "string" && pid === parentId) {
            result.push(id);
        }
    }
    return result;
}
export function setBlockParent(doc, blockId, parentId, relation) {
    doc.transact(() => {
        const meta = getBlockMetaMap(doc, blockId);
        if (parentId == null || parentId.length === 0) {
            meta.delete("parentId");
            meta.delete("relation");
        }
        else {
            const rootParent = resolveRootParentId(doc, parentId);
            meta.set("parentId", rootParent);
            if (relation != null && relation.length > 0) {
                meta.set("relation", relation);
            }
            else {
                meta.delete("relation");
            }
        }
        meta.set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
function insertAfterLastChildOrParent(doc, arr, parentId, newId) {
    let insertAt = arr.length;
    let lastChildIndex = -1;
    for (let i = 0; i < arr.length; i++) {
        const id = arr.get(i);
        if (id === parentId) {
            insertAt = i + 1;
        }
        if (typeof id === "string") {
            const meta = getBlockMetaMap(doc, id);
            const pid = meta.get("parentId");
            if (typeof pid === "string" && pid === parentId) {
                lastChildIndex = i;
            }
        }
    }
    if (lastChildIndex >= 0) {
        insertAt = lastChildIndex + 1;
    }
    arr.insert(insertAt, [newId]);
}
export function appendBlock(doc, kind, afterBlockId, opts) {
    const id = newBlockId();
    let payload = {};
    switch (kind) {
        case WaveBlockKind.Text:
            payload = { text: "" };
            break;
        case WaveBlockKind.Heading:
            payload = { text: "Titolo", level: 2 };
            break;
        case WaveBlockKind.Decision:
            payload = { title: "Decisione", choices: ["Sì", "No"] };
            break;
        case WaveBlockKind.Signoff:
            payload = { title: "Approvazione", approvals: {} };
            break;
        case WaveBlockKind.Poll:
            payload = {
                title: "Nuovo sondaggio",
                choices: ["Opzione A", "Opzione B", "Opzione C"],
            };
            break;
        case WaveBlockKind.Task:
            payload = { title: "Nuovo task", taskDone: false };
            break;
        case WaveBlockKind.TodoList:
            payload = { title: "Todo", todoItems: [] };
            break;
        case WaveBlockKind.Gallery:
            payload = { title: "Galleria", galleryItems: [] };
            break;
        case WaveBlockKind.TidePageRef:
            payload = { refId: "", refTitle: "Pagina Tide", refApp: "Tide" };
            break;
        case WaveBlockKind.DriftTodoRef:
            payload = { refId: "", refTitle: "Task Drift", refApp: "Drift" };
            break;
        case WaveBlockKind.ReefArticleRef:
            payload = { refId: "", refTitle: "Articolo Reef", refApp: "Reef" };
            break;
        case WaveBlockKind.PulseDocRef:
            payload = { refId: "", refTitle: "Progetto Pulse", refApp: "Pulse" };
            break;
        case WaveBlockKind.File:
            payload = {
                fileName: "",
                mimeType: "application/octet-stream",
                uploadStatus: "uploading",
                ragStatus: "none",
            };
            break;
    }
    const block = {
        id,
        kind,
        payload,
        sortIndex: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
    const parentId = opts?.parentId?.trim() || null;
    const relation = opts?.relation?.trim() || null;
    if (parentId) {
        const rootParent = resolveRootParentId(doc, parentId);
        block.payload.parentId = rootParent;
        if (relation)
            block.payload.relation = relation;
    }
    const arr = getBlockIdsArray(doc);
    doc.transact(() => {
        writePayloadToDoc(doc, block);
        if (kind === WaveBlockKind.TodoList) {
            getBlockMetaMap(doc, id).set("widgetIndex", nextTodoListWidgetIndex(doc));
        }
        if (parentId) {
            insertAfterLastChildOrParent(doc, arr, block.payload.parentId, id);
        }
        else if (afterBlockId) {
            let insertAt = arr.length;
            for (let i = 0; i < arr.length; i++) {
                if (arr.get(i) === afterBlockId)
                    insertAt = i + 1;
            }
            arr.insert(insertAt, [id]);
        }
        else {
            arr.push([id]);
        }
    }, LOCAL_YJS_ORIGIN);
    block.sortIndex = arr.toArray().indexOf(id);
    return block;
}
export function removeBlock(doc, blockId) {
    const arr = getBlockIdsArray(doc);
    doc.transact(() => {
        const childIds = getChildBlockIds(doc, blockId);
        for (const childId of childIds) {
            const childMeta = getBlockMetaMap(doc, childId);
            childMeta.delete("parentId");
            childMeta.delete("relation");
            childMeta.set("updatedAt", new Date().toISOString());
        }
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === blockId) {
                arr.delete(i, 1);
                break;
            }
        }
    }, LOCAL_YJS_ORIGIN);
}
function isBlockArchivedInDoc(doc, blockId) {
    return getBlockMetaMap(doc, blockId).get("archived") === true;
}
function computeBlockGroups(doc) {
    const arr = getBlockIdsArray(doc);
    const groups = [];
    let current = null;
    for (let i = 0; i < arr.length; i++) {
        const id = arr.get(i);
        if (typeof id !== "string")
            continue;
        const meta = getBlockMetaMap(doc, id);
        const parentId = meta.get("parentId");
        const hasParent = typeof parentId === "string" && parentId.length > 0;
        if (!hasParent) {
            if (current)
                groups.push(current);
            current = { topLevelId: id, ids: [id], startIndex: i, endIndex: i };
        }
        else if (current) {
            current.ids.push(id);
            current.endIndex = i;
        }
        else {
            groups.push({ topLevelId: id, ids: [id], startIndex: i, endIndex: i });
        }
    }
    if (current)
        groups.push(current);
    return groups;
}
function isActiveTopLevelGroup(doc, group) {
    return !isBlockArchivedInDoc(doc, group.topLevelId);
}
function adjustedInsertIndex(groupStart, groupLength, rawInsertAt) {
    if (groupStart < rawInsertAt) {
        return rawInsertAt - groupLength;
    }
    return rawInsertAt;
}
function relocateGroupInArray(arr, group, rawInsertAt) {
    const insertAt = adjustedInsertIndex(group.startIndex, group.ids.length, rawInsertAt);
    const ids = [...group.ids];
    for (let i = group.endIndex; i >= group.startIndex; i--) {
        arr.delete(i, 1);
    }
    arr.insert(insertAt, ids);
}
export function moveBlock(doc, blockId, direction) {
    const groups = computeBlockGroups(doc);
    const idx = groups.findIndex((g) => g.topLevelId === blockId);
    if (idx < 0)
        return false;
    const group = groups[idx];
    if (!isActiveTopLevelGroup(doc, group))
        return false;
    let targetIdx = -1;
    if (direction === "up") {
        for (let i = idx - 1; i >= 0; i--) {
            if (isActiveTopLevelGroup(doc, groups[i])) {
                targetIdx = i;
                break;
            }
        }
        if (targetIdx < 0)
            return false;
        const target = groups[targetIdx];
        const insertAt = target.startIndex;
        doc.transact(() => {
            relocateGroupInArray(getBlockIdsArray(doc), group, insertAt);
            getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
        }, LOCAL_YJS_ORIGIN);
        return true;
    }
    for (let i = idx + 1; i < groups.length; i++) {
        if (isActiveTopLevelGroup(doc, groups[i])) {
            targetIdx = i;
            break;
        }
    }
    if (targetIdx < 0)
        return false;
    const target = groups[targetIdx];
    const insertAt = target.endIndex + 1;
    doc.transact(() => {
        relocateGroupInArray(getBlockIdsArray(doc), group, insertAt);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
    return true;
}
export function moveBlockToIndex(doc, blockId, targetTopLevelBlockId, place) {
    if (blockId === targetTopLevelBlockId)
        return false;
    const groups = computeBlockGroups(doc);
    const sourceIdx = groups.findIndex((g) => g.topLevelId === blockId);
    const targetIdx = groups.findIndex((g) => g.topLevelId === targetTopLevelBlockId);
    if (sourceIdx < 0 || targetIdx < 0)
        return false;
    const source = groups[sourceIdx];
    const target = groups[targetIdx];
    if (!isActiveTopLevelGroup(doc, source) || !isActiveTopLevelGroup(doc, target)) {
        return false;
    }
    const insertAt = place === "before" ? target.startIndex : target.endIndex + 1;
    doc.transact(() => {
        relocateGroupInArray(getBlockIdsArray(doc), source, insertAt);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
    return true;
}
export function addTodoItem(doc, blockId, input = {}) {
    const id = newTodoItemId();
    const createdAt = new Date().toISOString();
    const item = {
        id,
        title: "",
        done: false,
        assignees: [],
        createdAt,
        authorSub: input.authorSub,
    };
    doc.transact(() => {
        writeTodoItemToDoc(doc, blockId, item);
        getBlockTodoItemsArray(doc, blockId).push([id]);
        getBlockMetaMap(doc, blockId).set("updatedAt", createdAt);
    }, LOCAL_YJS_ORIGIN);
    return item;
}
export function setTodoItemTitle(doc, blockId, itemId, title) {
    doc.transact(() => {
        const yText = getBlockTodoItemTitle(doc, blockId, itemId);
        const current = yText.toString();
        if (current === title)
            return;
        if (yText.length > 0)
            yText.delete(0, yText.length);
        if (title.length > 0)
            yText.insert(0, title);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function toggleTodoItemDone(doc, blockId, itemId, done) {
    doc.transact(() => {
        getBlockTodoItemMetaMap(doc, blockId, itemId).set("done", done);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function setTodoItemAssignees(doc, blockId, itemId, assignees) {
    doc.transact(() => {
        getBlockTodoItemMetaMap(doc, blockId, itemId).set("assignees", assignees);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function setTodoItemDueDate(doc, blockId, itemId, dueDateISO) {
    doc.transact(() => {
        const meta = getBlockTodoItemMetaMap(doc, blockId, itemId);
        if (dueDateISO == null)
            meta.delete("dueDateISO");
        else
            meta.set("dueDateISO", dueDateISO);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function setTodoItemPriority(doc, blockId, itemId, priority) {
    doc.transact(() => {
        const meta = getBlockTodoItemMetaMap(doc, blockId, itemId);
        if (priority == null)
            meta.delete("priority");
        else
            meta.set("priority", priority);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function removeTodoItem(doc, blockId, itemId) {
    doc.transact(() => {
        const arr = getBlockTodoItemsArray(doc, blockId);
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === itemId) {
                arr.delete(i, 1);
                break;
            }
        }
        getBlockTodoItemMetaMap(doc, blockId, itemId).clear();
        const title = getBlockTodoItemTitle(doc, blockId, itemId);
        if (title.length > 0)
            title.delete(0, title.length);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function reorderTodoItem(doc, blockId, itemId, toIndex) {
    doc.transact(() => {
        const arr = getBlockTodoItemsArray(doc, blockId);
        let fromIndex = -1;
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === itemId) {
                fromIndex = i;
                break;
            }
        }
        if (fromIndex < 0)
            return;
        arr.delete(fromIndex, 1);
        const clamped = Math.max(0, Math.min(toIndex, arr.length));
        arr.insert(clamped, [itemId]);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function addGalleryItem(doc, blockId, input = {}) {
    const id = newGalleryItemId();
    const createdAt = new Date().toISOString();
    const item = {
        id,
        fileName: input.fileName,
        mimeType: input.mimeType ?? "image/jpeg",
        s3Key: input.s3Key,
        fileSize: input.fileSize,
        uploadStatus: input.uploadStatus ?? "uploading",
        createdAt,
    };
    doc.transact(() => {
        writeGalleryItemToDoc(doc, blockId, item);
        getBlockGalleryItemsArray(doc, blockId).push([id]);
        getBlockMetaMap(doc, blockId).set("updatedAt", createdAt);
    }, LOCAL_YJS_ORIGIN);
    return item;
}
export function setGalleryItemMeta(doc, blockId, itemId, patch) {
    doc.transact(() => {
        const meta = getBlockGalleryItemMetaMap(doc, blockId, itemId);
        if (patch.s3Key !== undefined) {
            if (patch.s3Key == null)
                meta.delete("s3Key");
            else
                meta.set("s3Key", patch.s3Key);
        }
        if (patch.mimeType !== undefined) {
            if (patch.mimeType == null)
                meta.delete("mimeType");
            else
                meta.set("mimeType", patch.mimeType);
        }
        if (patch.fileName !== undefined) {
            if (patch.fileName == null)
                meta.delete("fileName");
            else
                meta.set("fileName", patch.fileName);
        }
        if (patch.fileSize !== undefined) {
            if (patch.fileSize == null)
                meta.delete("fileSize");
            else
                meta.set("fileSize", patch.fileSize);
        }
        if (patch.uploadStatus !== undefined) {
            if (patch.uploadStatus == null)
                meta.delete("uploadStatus");
            else
                meta.set("uploadStatus", patch.uploadStatus);
        }
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function setGalleryItemCaption(doc, blockId, itemId, caption) {
    doc.transact(() => {
        const yText = getBlockGalleryItemCaption(doc, blockId, itemId);
        const current = yText.toString();
        if (current === caption)
            return;
        if (yText.length > 0)
            yText.delete(0, yText.length);
        if (caption.length > 0)
            yText.insert(0, caption);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function removeGalleryItem(doc, blockId, itemId) {
    doc.transact(() => {
        const arr = getBlockGalleryItemsArray(doc, blockId);
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === itemId) {
                arr.delete(i, 1);
                break;
            }
        }
        getBlockGalleryItemMetaMap(doc, blockId, itemId).clear();
        const caption = getBlockGalleryItemCaption(doc, blockId, itemId);
        if (caption.length > 0)
            caption.delete(0, caption.length);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
export function reorderGalleryItem(doc, blockId, itemId, toIndex) {
    doc.transact(() => {
        const arr = getBlockGalleryItemsArray(doc, blockId);
        let fromIndex = -1;
        for (let i = 0; i < arr.length; i++) {
            if (arr.get(i) === itemId) {
                fromIndex = i;
                break;
            }
        }
        if (fromIndex < 0)
            return;
        arr.delete(fromIndex, 1);
        const clamped = Math.max(0, Math.min(toIndex, arr.length));
        arr.insert(clamped, [itemId]);
        getBlockMetaMap(doc, blockId).set("updatedAt", new Date().toISOString());
    }, LOCAL_YJS_ORIGIN);
}
/** @deprecated use getBlockText */
export function getBlockMap(doc, blockId) {
    return getBlockMetaMap(doc, blockId);
}
export function ensureBlockYText(doc, blockId, field) {
    return field === "title" ? getBlockTitle(doc, blockId) : getBlockText(doc, blockId);
}
export function ensureBlockYMap(doc, blockId, field) {
    return field === "votes"
        ? getBlockVotesMap(doc, blockId)
        : getBlockApprovalsMap(doc, blockId);
}
export function encodeYDocStateVectorB64(doc) {
    return uint8ToBase64(Y.encodeStateVector(doc));
}
/** Delta since a prior state vector (aligned with iOS `WaveYjsCodec.encodeDiffUpdateB64`). */
export function encodeDiffUpdateB64(doc, sinceStateVectorB64) {
    if (sinceStateVectorB64?.trim()) {
        const sv = base64ToUint8(sinceStateVectorB64);
        if (sv.length > 0) {
            return uint8ToBase64(Y.encodeStateAsUpdate(doc, sv));
        }
    }
    return encodeYDocUpdateB64(doc);
}
export function encodeYDocUpdateB64(doc) {
    const update = Y.encodeStateAsUpdate(doc);
    return uint8ToBase64(update);
}
export function applyYDocUpdateB64(doc, b64, origin = REMOTE_YJS_ORIGIN) {
    const trimmed = b64.trim();
    if (!trimmed)
        return;
    const bytes = base64ToUint8(trimmed);
    if (bytes.length === 0) {
        throw new Error("Yjs update base64 decode failed");
    }
    Y.applyUpdate(doc, bytes, origin);
}
export function encodeFullYDocSnapshotB64(doc) {
    return encodeYDocUpdateB64(doc);
}
export function uint8ToBase64(bytes) {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
export function base64ToUint8(b64) {
    try {
        const binary = atob(b64);
        const out = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            out[i] = binary.charCodeAt(i);
        }
        return out;
    }
    catch {
        return new Uint8Array(0);
    }
}
export function isYjsWaveFormat(formatVersion) {
    return formatVersion === "yjs-v1";
}
